import {Box, createStyles, Text} from '@mantine/core';
import React from 'react';

import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {formatConjugationSiteResidue} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import Chip, {ChipSize} from 'ui-library/chips/Chip';

const useStyles = createStyles((theme) => ({
  // Lays out the domain and residue as a Group would; Group always renders a div, which a chip's text can't hold.
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    whiteSpace: 'nowrap',
  },
  domain: {
    fontSize: theme.fontSizes.xxs,
    fontWeight: 700,
    color: theme.colors.gray[6],
  },
  residue: {
    fontFamily: theme.fontFamilyMonospace,
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    color: theme.colors.blue[7],
  },
}));

interface AdcConjugationSiteChipProps {
  site: ConjugationSite;
  /** The antibody's domains, used to label the site's residue; see formatConjugationSiteResidue. */
  domains?: ReadonlyArray<AntibodyDomain> | null;
  /** Called when the chip is dismissed; the chip can't be dismissed without it. */
  onRemove?: (() => void) | null;
}

/** A conjugation site's chip: its domain, then its residue, e.g. "Hinge C220". */
export default function AdcConjugationSiteChip({
  site,
  domains = null,
  onRemove = null,
}: AdcConjugationSiteChipProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <Chip
      size={ChipSize.TABLE_CELL}
      isReadonly={true}
      onRemove={onRemove}
      text={
        <Box component="span" className={classes.label}>
          <Text component="span" className={classes.domain}>
            {site.domainLabel}
          </Text>
          {/* Takes no space in the row, but separates the domain and residue in the chip's text. */}{' '}
          <Text component="span" className={classes.residue}>
            {formatConjugationSiteResidue(site, domains)}
          </Text>
        </Box>
      }
    />
  );
}
